import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { createSelector, createSliceHook } from '../helpers';

type ReaderCacheEntry<T> = { data: T; fetchedAt: number };

export type ReaderCacheState = {
  ctContext: Record<string, ReaderCacheEntry<unknown>>;
  inventory: Record<string, ReaderCacheEntry<unknown>>;
  dicomResource: Record<string, ReaderCacheEntry<unknown>>;
  parentTaskCache: Record<string, { subtasks: Record<string, ReaderCacheEntry<unknown>> }>;
};

const initialState: ReaderCacheState = {
  ctContext: {},
  inventory: {},
  dicomResource: {},
  parentTaskCache: {},
};

type TrialIdPayload = { clinicalTrialId: string; data: unknown };
type SubtaskPayload = { parentTaskId: string; subTaskId: string; data: unknown };
type FlushParentPayload = { parentTaskId: string };
type FlushTrialPayload = { clinicalTrialId: string };
type FlushSubtaskPayload = { parentTaskId: string; subTaskId: string };

export const readerCacheSlice = createSlice({
  name: 'readerCache',
  initialState,
  reducers: {
    setCtContext: (state, action: PayloadAction<TrialIdPayload>) => {
      state.ctContext[action.payload.clinicalTrialId] = {
        data: action.payload.data,
        fetchedAt: Date.now(),
      };
    },
    setInventory: (state, action: PayloadAction<TrialIdPayload>) => {
      state.inventory[action.payload.clinicalTrialId] = {
        data: action.payload.data,
        fetchedAt: Date.now(),
      };
    },
    setDicomResource: (state, action: PayloadAction<TrialIdPayload>) => {
      state.dicomResource[action.payload.clinicalTrialId] = {
        data: action.payload.data,
        fetchedAt: Date.now(),
      };
    },
    setSubtask: (state, action: PayloadAction<SubtaskPayload>) => {
      const { parentTaskId, subTaskId, data } = action.payload;
      const bucket = state.parentTaskCache[parentTaskId] ?? { subtasks: {} };
      bucket.subtasks[subTaskId] = { data, fetchedAt: Date.now() };
      state.parentTaskCache[parentTaskId] = bucket;
    },
    flushParentTask: (state, action: PayloadAction<FlushParentPayload>) => {
      delete state.parentTaskCache[action.payload.parentTaskId];
    },
    flushTrial: (state, action: PayloadAction<FlushTrialPayload>) => {
      const { clinicalTrialId } = action.payload;
      delete state.ctContext[clinicalTrialId];
      delete state.inventory[clinicalTrialId];
      delete state.dicomResource[clinicalTrialId];
    },
    flushSubtask: (state, action: PayloadAction<FlushSubtaskPayload>) => {
      const { parentTaskId, subTaskId } = action.payload;
      const bucket = state.parentTaskCache[parentTaskId];
      if (bucket) {
        delete bucket.subtasks[subTaskId];
      }
    },
    flushAll: () => initialState,
  },

  selectors: {
    selectCtContext: (state: ReaderCacheState, id: string) => state.ctContext[id]?.data,
    selectInventory: (state: ReaderCacheState, id: string) => state.inventory[id]?.data,
    selectDicomResource: (state: ReaderCacheState, id: string) => state.dicomResource[id]?.data,
    selectSubtask: (state: ReaderCacheState, parentTaskId: string, subTaskId: string) =>
      state.parentTaskCache[parentTaskId]?.subtasks[subTaskId]?.data,
    selectCachedSubtaskIds: (state: ReaderCacheState, parentTaskId: string) =>
      new Set(Object.keys(state.parentTaskCache[parentTaskId]?.subtasks ?? {})),
  },
});

export const actions = { ...readerCacheSlice.actions };

export const useReaderCacheSlice = createSliceHook(readerCacheSlice.name, actions);

export const selectReaderCacheState = createSelector('readerCache');

type ReaderCacheRoot = Parameters<typeof selectReaderCacheState>[0];

// Injection-tolerant accessors. `readerCache` is injected only in the reader
// role; in other roles (admin/pm, QC) the slice is absent and reading
// `readerCacheSlice.selectors.*` throws "selectSlice returned undefined for an
// uninjected slice reducer". These read through `selectReaderCacheState` (which
// yields `undefined` when the slice is not injected) and therefore never throw —
// callers treat a cache miss and an absent cache identically.
export const selectIsReaderCacheActive = (state: ReaderCacheRoot): boolean =>
  !!selectReaderCacheState(state);

export const selectCachedCtContext = (state: ReaderCacheRoot, id: string) =>
  selectReaderCacheState(state)?.ctContext[id]?.data;

export const selectCachedInventory = (state: ReaderCacheRoot, id: string) =>
  selectReaderCacheState(state)?.inventory[id]?.data;

export const selectCachedDicomResource = (state: ReaderCacheRoot, id: string) =>
  selectReaderCacheState(state)?.dicomResource[id]?.data;

export const selectCachedSubtask = (
  state: ReaderCacheRoot,
  parentTaskId: string,
  subTaskId: string
) => selectReaderCacheState(state)?.parentTaskCache[parentTaskId]?.subtasks[subTaskId]?.data;

export const selectCachedSubtaskIds = (
  state: ReaderCacheRoot,
  parentTaskId: string
): Set<string> =>
  new Set(
    Object.keys(selectReaderCacheState(state)?.parentTaskCache[parentTaskId]?.subtasks ?? {})
  );
